import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  ConsentStepRibbon,
  type ConsentStepNumber,
} from '../../../components/parent/ConsentStepRibbon';
import './ConsentLayout.css';

/**
 * Wrapper used by every consent flow screen (Steps 1–7).
 *
 * Sits OUTSIDE the parent-shell + outside the AuthGate — the parent
 * doesn't have an account yet during Steps 1–4 (Step 1–3 child-side;
 * Step 4 parent-side via email link, no auth). Steps 5–7 still use
 * this layout post-Yoti since the visual continuity matters more than
 * the auth boundary.
 *
 * Tone: warm, calm, focused. Single column. The ribbon up top is
 * the only chrome; the rest is just the form / content for the
 * current step.
 */

export interface ConsentLayoutProps {
  step: ConsentStepNumber;
  /** Short page title above the body. */
  title: string;
  /** Optional subtitle / lead paragraph below the title. */
  lead?: ReactNode;
  /** Body content — the form / explanation for this step. */
  children: ReactNode;
  /** Optional footer override. By default we render legal links + a
   *  "need help" line. Pass `null` to suppress (rarely needed). */
  footer?: ReactNode | null;
}

export function ConsentLayout({ step, title, lead, children, footer }: ConsentLayoutProps) {
  return (
    <div className="consent-layout">
      <header className="consent-layout__top">
        <Link to="/" className="consent-layout__brand">
          4CM Coaching
        </Link>
      </header>

      <ConsentStepRibbon active={step} />

      <main className="consent-layout__main">
        <header className="consent-layout__head">
          <h1 className="consent-layout__title">{title}</h1>
          {lead && <p className="consent-layout__lead">{lead}</p>}
        </header>

        <div className="consent-layout__body">{children}</div>
      </main>

      {footer === undefined ? (
        <footer className="consent-layout__footer">
          <Link to="/legal/privacy" className="consent-layout__legal-link">
            Privacy notice
          </Link>
          <Link to="/legal/privacy-for-young-players" className="consent-layout__legal-link">
            For young players
          </Link>
          <span className="consent-layout__help">
            Stuck?{' '}
            <a href="mailto:welfare@4cmcoaching.co.uk">welfare@4cmcoaching.co.uk</a>
          </span>
        </footer>
      ) : (
        footer
      )}
    </div>
  );
}
